import axios, { AxiosError } from 'axios'

import { ApiError, ServiceError } from './ServiceError'
import { SolarResponse } from './solar'
import { SupernovaError } from './supernova'

export class AxiosErrorHandler {
  handle = (error: unknown): never => {
    if (axios.isAxiosError(error)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { response } = error as AxiosError<any>

      // 일반적으로 response가 없거나 response가 객체가 아닌 경우는 네트워크 오류 또는 CORS 오류로 간주합니다.
      if (!response || typeof response !== 'object') {
        console.error('Network/CORS Error', { error }, { config: error.config })

        throw {
          status: error.response?.status ?? null,
          code: error.code === 'ERR_NETWORK' ? 'NetworkError' : 'CORSError',
          message:
            error.code === 'ERR_NETWORK'
              ? '네트워크 연결에 실패했습니다.'
              : 'CORS 설정으로 인해 요청이 거부되었습니다.',
          details: [
            {
              code: error.code || 'UNKNOWN',
              message: error.message || '알 수 없는 오류가 발생했습니다.',
            },
          ],
        }
      }

      // 일반적으로 response.data가 없거나 response.data가 객체가 아닌 경우는 서버 응답이 올바르지 않은 경우로 간주합니다.
      if (!response.data || typeof response.data !== 'object') {
        console.error('Invalid Response Data', { error }, { config: error.config })
        throw {
          status: response.status,
          code: 'InvalidResponse',
          message: '서버 응답이 올바르지 않습니다.',
          details: [
            {
              code: 'INVALID_RESPONSE_DATA',
              message: `응답 데이터가 올바르지 않습니다: ${JSON.stringify(response.data)}`,
            },
          ],
        }
      }

      console.error('Handled Service Error Detected', { response }, { originError: JSON.stringify(error) })

      if ('timestamp' in response.data) {
        throw this.handleSupernovaError(response.status, response.data as SupernovaError)
      }

      if ('error' in response.data) {
        throw this.handleServiceError(response.status, response.data as ServiceError)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if ('succeeded' in response.data && response.data.succeeded === false) {
        throw this.handleSolarError(response.status, response.data as SolarResponse<unknown>)
      }
    }

    console.error(
      'Unexpected Code Exception Detected',
      {
        message: (error as Error)?.message ?? '',
        error: JSON.stringify(error),
      },
      error,
    )

    throw error
  }

  private handleServiceError = (status: number, data: ServiceError): ApiError => {
    return {
      status,
      code: data.error.code,
      message: data.error.message,
      details: data.error.details ?? [],
    }
  }

  private handleSolarError = (status: number, data: SolarResponse<unknown>): ApiError => {
    return {
      status,
      code: data.msgCode ?? '',
      message: data.reason ?? '',
      details: [],
    }
  }

  private handleSupernovaError = (status: number, data: SupernovaError): ApiError => {
    return {
      status: data.status ?? status,
      code: data.error ?? '',
      message: data.message ?? '',
      details: [],
    }
  }
}
